/** @type {import('tailwindcss').Config} */

const { devConsoleLog } = require('./src/utils/logging');
const backgroundImages = require('./src/styles/backgroundImages');

module.exports = {
  content: ['./src/**/*.{js,ts,jsx,tsx}'],
  theme: {
    screens: {
      sm: '300px',
      md: '768px',
      lg: '1024px',
      xl: '1240px',
      '2xl': '1440px',
    },
    fontFamily: {
      sans: [
        'BrownPro',
        'HelveticaNeue',
        'Helvetica Neue',
        'Helvetica',
        'Arial',
        'sans-serif',
      ],
      bradford: ['BradfordLLWeb-MediumItalic'],
    },
    extend: {
      colors: {
        warning: '#D52300',
        success: '#19883E',
        charcoal: '#514f4e',
        'charcoal-v2': '#424140',
        'charcoal-dark': '#3B3A39',
        'charcoal-opacity-20': '#514f4e33',
        'grey-midtone': '#9AA5AF',
        'grey-midtone-light': '#DCD7D2',
        'grey-light': '#F4F4F4',
        'grey-light-badge': '#6E757C',
        'dark-green': '#17850D',
        'brand-pastel': '#bdedef',
        grey: {
          DEFAULT: '#DDD',
          dark: '#424140',
          light: '#6E757C',
          'mid-light': '#E5E7EB',
          pastel: '#F4F4F4',
        },
        indigo: {
          DEFAULT: '#536DC4',
          pastel: '#F5F7FC',
          tint: '#EDF0F9',
          dark: '#3B54AB',
          'extra-dark': '#131C66',
        },
        aqua: {
          DEFAULT: '#4EDCCA',
          tint: '#EFFAF9',
        },
        'kelly-green': {
          DEFAULT: '#29CC5F',
          tint: '#EAF9F0',
          pastel: '#E5F6E3',
        },
        'lime-green': {
          DEFAULT: '#BBDC00',
          tint: '#F5FAEA',
        },
        yellow: {
          DEFAULT: '#FEDB00',
          tint: '#FCF9EB',
          pastel: '#F9F8F3',
        },
        orange: {
          DEFAULT: '#FF9955',
          tint: '#F8F4EC',
        },
        red: {
          DEFAULT: '#FF5E3E',
          tint: '#F9EFEC',
          v2: '#DB350F',
          'v2-hover': '#832009',
        },
        pink: {
          DEFAULT: '#E44C9A',
          tint: '#F7F3F7',
        },
        purple: {
          DEFAULT: '#B85BBF',
          tint: '#F7F3F7',
        },
        teal: {
          DEFAULT: '#EFFBF9',
        },
        tan: {
          DEFAULT: '#f9f7f4',
          tint: '#fffbe8',
          pastel: '#F9F7F5',
        },
        khaki: {
          tint: '#F8F6F1',
        },
        blue: {
          tint: '#DFF5F3',
          deep: '#202EA8',
          'deep-hover': '#131C66',
          light: '#F2FAFF',
        },
        'off-white': {
          DEFAULT: '#F9F8F3',
        },
        maroon: {
          DEFAULT: '#3C001A',
          dark: '#360019',
          'dark-hover': '#20000f',
        },
      },
      fontSize: {
        'd-sm': ['1rem', '1.25rem'],
        'd-base': ['1.125rem', '1.5rem'],
        'd-lg': ['1.25rem', '2rem'],

        // New Heading sizes list (https://www.figma.com/design/ZkcgBWJkDsVosnxbw7Vn0k/Design-System?node-id=6701-9105&m=dev)
        // Core
        'h-x6': ['1.125rem', '1.625rem'],
        'h-x5': ['1.25rem', '1.75rem'],
        'h-x4': ['1.5rem', '2rem'],
        'h-x3': ['2rem', '2.5rem'],
        'h-x2': ['2.5rem', '3rem'],
        'h-x1': ['3rem', '3.5rem'],
        'h-xl': ['4rem', '4.5rem'],
        // Support
        'hs-x6': ['21px', '1.625rem'],
        'hs-x5': ['23px', '1.75rem'],
        'hs-x4': ['27px', '2rem'],
        'hs-x3': ['35px', '2.5rem'],
        'hs-x2': ['43px', '3rem'],
        'hs-x1': ['51px', '3.5rem'],
        'hs-xl': ['67px', '4.5rem'],
      },
      dropShadow: {
        le: '0px 3px 16px rgba(0, 0, 0, 0.2)',
      },
      gridTemplateColumns: {
        'explorer-slice': '1fr auto',
      },
      gridTemplateRows: {
        auto: 'auto',
      },
      // Leverages CSS grid-template-areas under the hood.
      gridTemplateAreas: {
        usSmall: [
          'learn',
          'products',
          'help',
          'gifting',
          'podcast',
          'app',
          'social',
          'selectors',
          'credentials',
        ],
        usMedium: [
          'learn help',
          'learn gifting',
          'products podcast',
          'app social',
          'selectors .',
          'credentials .',
        ],
        usLarge: [
          'learn products gifting help app',
          'learn products gifting help social',
          'learn products gifting podcast selectors',
          'learn products gifting podcast credentials',
        ],
        usXLarge: [
          'learn products gifting help podcast app',
          'learn products gifting help podcast social',
          'learn products gifting help podcast selectors',
          'learn products gifting help podcast credentials',
        ],
        auSmall: [
          'contact',
          'learn',
          'products',
          'help',
          'social',
          'selectors',
          'credentials',
        ],
        auMedium: [
          'contact .',
          'learn help',
          'products social',
          'selectors .',
          'credentials .',
        ],
        auLarge: [
          'learn products help social',
          'learn products help selectors',
          'learn products help credentials',
        ],
        auXLarge: [
          'learn products help social',
          'learn products help selectors',
          'learn products help credentials',
        ],
        euSmall: [
          'learn',
          'products',
          'help',
          'gifting',
          'podcast',
          'app',
          'social',
          'selectors',
          'credentials',
        ],
        euMedium: [
          'contact .',
          'learn help',
          'learn help',
          'products social',
          'products social',
          'selectors .',
          'credentials .',
        ],
        euLarge: [
          'learn products help contact selectors',
          'learn products help social selectors',
          'learn products help social selectors',
          'learn products help social credentials',
        ],
        euXLarge: [
          'learn products help contact selectors',
          'learn products help social selectors',
          'learn products help social selectors',
          'learn products help social credentials',
        ],
      },
      aspectRatio: {
        '3/2': '3 / 2',
        video: '16 / 9',
        square: '1 / 1',
      },
      borderWidth: {
        18: '18px',
      },
      backgroundImage: backgroundImages,
      transitionProperty: {
        width: 'width',
      },
      boxShadow: {
        'sticky-cta':
          'rgba(0, 0, 0, 0.12) 0px 0px 1px 0px, rgba(0, 0, 0, 0.16) 0px -2px 3px 0px',
        card: '0px 0px 5px 0px #00000059',
      },
      zIndex: {
        999: '999',
      },
      keyframes: {
        rise: {
          from: { height: '100%' },
          to: { height: '0%' },
        },
        'slide-in-left': {
          from: { transform: 'translateX(-100%)' },
          to: { transform: 'translateX(0)' },
        },
        'slide-in-right': {
          from: { transform: 'translateX(100%)' },
          to: { transform: 'translateX(0)' },
        },
        'slide-out-left': {
          from: { transform: 'translateX(0)' },
          to: { transform: 'translateX(-100%)' },
        },
        'slide-out-right': {
          from: { transform: 'translateX(0)' },
          to: { transform: 'translateX(100%)' },
        },
      },
      animation: {
        rise: 'rise 1s ease-out forwards',
        'slide-in-left': 'slide-in-left 0.2s ease-in-out',
        'slide-in-right': 'slide-in-right 0.2s ease-in-out',
        'slide-out-left': 'slide-out-left 0.2s ease-in-out',
        'slide-out-right': 'slide-out-right 0.2s ease-in-out',
      },
      listStyleImage: {
        purpleCircle:
          'url("//images.ctfassets.net/0sea1vycfyqy/7HhYyUuINfKc4iM17HHUp9/9fbc1056256a913c37bae5f96e05700b/Circle_01.png")',
        greenCheck:
          'url("//images.ctfassets.net/0sea1vycfyqy/6uxFIDIxdK75AZEZ5CjYj4/520ab3acee3d0f3c441f990bd33fece4/CheckMark_Brushstroke.png")',
      },
    },
  },
  plugins: [
    require('daisyui'),
    require('@savvywombat/tailwindcss-grid-areas'),
    require('@tailwindcss/aspect-ratio'),
  ],
  daisyui: {
    styled: false,
    themes: [],
    utils: true,
  },
  safelist: [
    {
      pattern: _generateSafelistPattern(),
    },
    {
      pattern: /text-(left|right|center)/,
      variants: ['sm', 'md', 'lg', 'xl', '2xl'],
    },
    {
      pattern: /justify-(start|center|end)/,
      variants: ['sm', 'md', 'lg', 'xl', '2xl'],
    },
    {
      pattern: /gap/,
      variants: ['sm', 'md', 'lg', 'xl', '2xl'],
    },
  ],
};

/**
 * Any styles that are configurable in contentful (i.e. colors, spacing) are subject to tree shaking
 * We need to inform tailwind to include the styles that our customizations could result in.
 *
 * @returns {RegExp}
 */
function _generateSafelistPattern() {
  const customizations = [
    {
      type: 'colors',
      prefixes: ['bg', 'border-b', 'border-t', 'stroke'],
      variants: ['sm', 'md', 'lg', 'xl', '2xl'],
      roots: [
        'white',
        'charcoal',
        'grey',
        'aqua',
        'indigo',
        'kelly-green',
        'lime-green',
        'orange',
        'yellow',
        'red',
        'pink',
        'purple',
        'tan',
        'teal',
        'khaki',
        'off-white',
      ],
    },
    {
      type: 'spacing',
      prefixes: ['gap', 'my', 'py'],
      roots: ['4', '6', '8', '10', '12'],
    },
    {
      type: 'textSizesAndLeading',
      prefixes: ['text', 'leading'],
      variants: ['sm', 'md', 'lg', 'xl', '2xl'],
      roots: ['\\[\\d+px\\]'],
    },
  ];

  const customStyles = customizations.flatMap((customization) =>
    _generateCustomizationRegex(customization),
  );
  return new RegExp(customStyles.join('|'));
}

function _generateCustomizationRegex(customization) {
  const rootsPart = `(?=${customization.roots.join('|')})`;

  return customization.prefixes.flatMap((prefix) => `${prefix}-${rootsPart}`);
}
